/**
 * How many couples one page of a count's list holds (docs/pipeline-view.spec.md,
 * "The couple list"). Fixed rather than asked for: a page's names that only
 * GoHighLevel knows are looked up live, one request each, so the page stays
 * small enough to answer quickly within its rate limit.
 */
export const PIPELINE_COUPLES_PAGE_SIZE = 25

/**
 * The fewest couples a priced count needs before it gets a cost
 * (docs/pipeline-view.spec.md, "Costs"). Below it, a cost is most of the
 * period's spend pinned on one or two couples, which reads as a fact and isn't
 * one.
 */
export const PIPELINE_MIN_PRICED_COUNT = 3

/**
 * How a step is judged against what's usual (docs/pipeline-view.spec.md,
 * "What's usual"). The usual needs at least `PIPELINE_MIN_BASELINE` couples at
 * the step before in the year before the span; the venue's timing, at least
 * `PIPELINE_MIN_TIMED` couples whose days are known.
 */
export const PIPELINE_MIN_BASELINE = 20
export const PIPELINE_MIN_TIMED = 10

/**
 * A step reads below or above usual only when its couples are this many
 * standard deviations from the usual by now (about one chance in ten, either
 * way, of happening by luck alone) and at least `PIPELINE_MIN_GAP` couples
 * apart, out of at least `PIPELINE_MIN_JUDGED`. A flag that is often wrong is
 * soon ignored.
 */
export const PIPELINE_JUDGE_Z = 1.645
export const PIPELINE_MIN_GAP = 2
export const PIPELINE_MIN_JUDGED = 10

/** How a step compares with what's usual by now. */
export const PIPELINE_VERDICTS = ['below', 'usual', 'above'] as const
export type PipelineVerdict = (typeof PIPELINE_VERDICTS)[number]

/**
 * The couples a Pipeline page points at, beyond the ones behind a count
 * (docs/pipeline-view.spec.md, "Couples to look at"):
 *
 * - `toured_deciding` / `toured_lost`: the span's couples who toured and
 *   haven't booked, still deciding or lost.
 * - `toured_waiting`: any couple who toured three weeks to six months ago and
 *   has neither booked nor been lost.
 * - `holds_ending`: couples whose hold ends within a week, not yet booked.
 * - `quiet_before_tour`: couples lost to silence before a tour in the last 90 days.
 */
export const PIPELINE_COUPLE_LISTS = ['toured_deciding', 'toured_lost', 'toured_waiting', 'holds_ending', 'quiet_before_tour'] as const
export type PipelineCoupleList = (typeof PIPELINE_COUPLE_LISTS)[number]

/** Days since a tour before a couple who hasn't decided is waiting on someone: three weeks. */
export const PIPELINE_WAITING_DAYS = 21
/** Past six months, a couple who toured and went quiet isn't waiting any more. */
export const PIPELINE_WAITING_LIMIT_DAYS = 183
/** How many days, today first, a hold's last day counts as ending soon in: a week. */
export const PIPELINE_HOLDS_ENDING_DAYS = 7
/** How far back a loss to silence before a tour is still worth a second try. */
export const PIPELINE_QUIET_DAYS = 90
